"use client";

import React from "react";
import { motion } from "framer-motion";

const cormorant = {
    className: "font-cormorant font-serif",
};

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.7 },
};

export interface CollabItem {
    title: string;
    description: string;
}

export interface GuestiqoCollaborationBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    items?: CollabItem[];
}

export default function GuestiqoCollaborationBlock({
    tag = "// Execution Model",
    title = "The Collaboration Model",
    description = "We worked in dedicated product squad intervals to design, iterate, code, and deploy in weekly review cadences.",
    items = [
        { title: "Dedicated Squad", description: "A tailored, interdisciplinary unit of 4 — product architect, UI/UX designer, frontend engineer, and database engineer — committed exclusively to Guestiqo." },
        { title: "Weekly Sprints", description: "Weekly review cycles starting with Monday plans, mid-week feedback standups, and Friday demo updates. Deliverables were tested in staging sandbox urls before release." },
        { title: "Automatic CI/CD Deploy", description: "Pull request review deployments triggered on Git merges, automatically generating isolated sandbox environments for verification." }
    ]
}: GuestiqoCollaborationBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#FAF7F2] border-t border-b border-[#1C1C1C]/5 w-full" data-header-theme="dark">
            <div className="container mx-auto">
                <div className="max-w-3xl mb-16">
                    {tag && <span className="text-xs font-mono text-[#8C8276] uppercase tracking-widest block mb-4">{tag}</span>}
                    {title && <h2 className={`${cormorant.className} text-4xl md:text-6xl font-light text-[#1C1C1C] leading-tight`}>{title}</h2>}
                    {description && (
                        <p className="text-[#5E5E5E] text-sm mt-3 font-sans font-light max-w-xl">
                            {description}
                        </p>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {items.map((collab, i) => (
                        <motion.div 
                            key={i}
                            {...fadeUp}
                            transition={{ delay: i * 0.1 }}
                            className="p-8 bg-[#FDFBF7] border border-[#1C1C1C]/10 rounded-2xl flex flex-col justify-between"
                        >
                            <h3 className={`${cormorant.className} text-2xl text-[#1C1C1C] font-semibold mb-4`}>
                                {collab.title}
                            </h3>
                            <p className="text-xs text-[#5E5E5E] leading-relaxed font-sans font-light">
                                {collab.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
